// The API's structured error shape, as a throwable.
//
// `message` is human text the API guarantees is safe to display, so screens surface
// it verbatim. A 422 `validation_failed` carries its per-field messages in
// `details.errors`; `fieldErrors` returns them typed.

import type { FieldError } from '@glasses/shared'

export interface ApiErrorBody {
  code: string
  message: string
  details?: Record<string, unknown>
  request_id?: string
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: Record<string, unknown>
  readonly requestId?: string

  constructor(status: number, body: ApiErrorBody) {
    super(body.message || body.code || `HTTP ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.code = body.code
    this.details = body.details
    this.requestId = body.request_id
  }

  /** The per-field messages of a 422 `validation_failed`; empty for any other error. */
  get fieldErrors(): FieldError[] {
    const list = this.details?.errors
    return Array.isArray(list) ? (list as FieldError[]) : []
  }

  /** A state conflict the user can act on. */
  get isConflict(): boolean {
    return this.status === 409
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError
}

/** The message to show a user for any thrown value. */
export function errorMessage(e: unknown): string {
  if (isApiError(e)) return e.message
  if (e instanceof Error) return e.message
  return String(e)
}
